import { AfterViewInit, Component, computed, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '../../../shared/components/breadcrumbs/breadcrumbs';
import { CreateSharedLinkRequestDto, SharedTestType } from '../../../shared/models/share-tests.model';
import { TestResult } from '../../../shared/models/test-result.model';
import { Role } from '../../../shared/models/user.model';
import { SearchParams } from '../../../shared/services/live-number-testing.service';
import { CreateTestGroup, ManualNumberTestingService } from '../../../shared/services/manual-number-testing.service';
import { DialogRef, ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PricingService } from '../../../shared/services/pricing.service';
import { ReimburseUtils } from '../../../shared/services/reimburse-utils';
import { ShareTestResultsUtils } from '../../../shared/services/share-test-results-utils';
import { UsersService } from '../../../shared/services/users.service';
import { MntFormComponent } from '../mnt-form/mnt-form.component';
import { MntResultsTableComponent } from '../mnt-results-table/mnt-results-table.component';
import { MntSearchFormComponent } from '../mnt-search-form/mnt-search-form.component';

@Component({
  standalone: false,
  selector: 'app-mnt-index',
  templateUrl: 'mnt-index.component.html',
  styleUrls: ['mnt-index.component.scss'],
})
export class MntIndexComponent implements OnInit, AfterViewInit {
  @ViewChild(MntFormComponent, { static: false }) form: MntFormComponent;
  @ViewChild(MntResultsTableComponent, { static: false }) resultsTable: MntResultsTableComponent;
  @ViewChild(MntSearchFormComponent, { static: false }) filter: MntSearchFormComponent;

  @ViewChild('shareTestsModalTpl', { read: TemplateRef, static: false }) shareTestsModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('batchRepeatModal', { read: TemplateRef, static: false }) batchRepeatModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('reimburseModalTpl', { read: TemplateRef, static: false }) reimburseModalTpl: TemplateRef<HTMLTemplateElement>;
  batchRepeatModalDialogRef: DialogRef;

  pauseUpdateForm = false;
  pauseUpdateResults = false;
  isBatchRepeatLoading = false;
  batchCompleted = 0;
  testsPrice = 0;
  testingDisabled = null;
  isAdmin = false;

  // signals
  readonly selectedRows = signal<TestResult[]>([]);

  // computed
  readonly hasReimbursableTests = computed(() => this.selectedRows().some(row => ReimburseUtils.canReimburse(row)));

  readonly shareTestResultsInfo = computed<CreateSharedLinkRequestDto>(() =>
    ShareTestResultsUtils.prepareShareTestResultsInfo(this.selectedRows(), SharedTestType.MNT)
  );
  readonly hasForbiddenTestsToShare = computed(() => ShareTestResultsUtils.hasForbiddenTestsToShare(this.selectedRows(), SharedTestType.MNT));
  readonly shareTestTooltip = computed(() => ShareTestResultsUtils.getShareTestsTooltipLabel(this.selectedRows(), this.hasForbiddenTestsToShare()));

  constructor(
    public breadcrumbs: BreadcrumbService,
    public router: Router,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    public userService: UsersService,
    private modal: ModalService,
    private manualTestingService: ManualNumberTestingService,
    private pricingService: PricingService,
    titleService: Title
  ) {
    titleService.setTitle('Manual testing');
  }

  afterSaveTestCase() {
    this.pauseUpdateResults = false;
    this.resultsTable.currentPageNumber = 1;
    this.resultsTable.update();
  }

  ngOnInit() {
    this.breadcrumbs.root('Dashboard', ['dashboard']).add('Test results', ['result-test/manual'], true);

    this.userService.getAuthUser().then(u => {
      this.isAdmin = u.role === Role.ADMIN;
    });
  }

  ngAfterViewInit() {
    this.route.queryParams.subscribe(queryParams => {
      const phoneNumber = queryParams.phoneNumber as string | undefined;

      if (!phoneNumber) return;

      const interval = setInterval(() => {
        if (!this.resultsTable) {
          return;
        }
        clearInterval(interval);

        this.filter.filters.forEach(_ => {
          if (_.filterKey === 'searchByPhoneNumbers' && phoneNumber) _.show = true;
        });

        if (phoneNumber) {
          this.filter.model.phoneNumbers = phoneNumber;
          this.filter.form.patchValue({ phoneNumbers: phoneNumber }, { emitEvent: false });
          const searchParams = this.filter.getPopulatedModel();
          this.resultsTable.searchParams = searchParams;
        }

        this.resultsTable.currentPageNumber = 1;
        this.resultsTable.update();
      }, 1000);
    });
  }

  onSubmitFilter(event: SearchParams) {
    if (this.resultsTable.loading) {
      return;
    }
    this.resultsTable.searchParams = event;
    this.resultsTable.currentPageNumber = 1;
    this.resultsTable.update();
  }

  batchClear(): void {
    this.selectedRows.set([]);
    this.resultsTable.resetBatch();
  }

  onBatchRepeat(): void {
    this.isBatchRepeatLoading = true;
    this.batchRepeatModalDialogRef = this.modal.alert().dialogClass('modal-dialog extra-large-modal').component(this.batchRepeatModalTpl).open();
    this.pricingService.getTestPrices(this.selectedRows().length).subscribe({
      next: (result: number) => {
        this.testsPrice = result;
        this.isBatchRepeatLoading = false;
      },
    });
  }

  onBatchReimburse(): void {
    const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.reimburseModalTpl).open();
    dialogRef.result.then(_ => {
      if (_) {
        const reimbursableTests = this.selectedRows().filter(row => ReimburseUtils.canReimburse(row));
        const testIds = reimbursableTests.map(_ => _.id);
        this.manualTestingService.reimburse(testIds).subscribe({
          next: () => {
            this.notificationService.success('Test cases reimbursed', 'Manual Testing');
            this.resultsTable.update();
            this.batchClear();
          },
          error: () => {
            this.notificationService.error('Test cases reimbursement failed', 'Manual Testing');
          },
        });
      }
    });
  }

  onShareTestsClick() {
    this.modal.alert().dialogClass('modal-dialog tablet-modal').component(this.shareTestsModalTpl).open();
  }

  onRepeatClick(): void {
    this.isBatchRepeatLoading = true;
    const testGroup: CreateTestGroup = {
      testDtos: this.selectedRows().map(_ => ({ destinationDto: _.destinationDto })),
    };
    this.manualTestingService.run(testGroup).subscribe({
      next: () => {
        this.notificationService.success('Tests repeated successfully', 'Manual testing');
        this.batchRepeatModalDialogRef.close();
        this.isBatchRepeatLoading = false;
        this.batchClear();
        this.resultsTable.update();
      },
      error: error => {
        console.error('Error in repeating: ', error);
        this.notificationService.success('Tests repeated successfully', 'Manual testing');
        this.isBatchRepeatLoading = false;
        this.batchClear();
      },
    });
  }
}
